import {
  BufferAttribute,
  DoubleSide,
  Group,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  MeshBasicNodeMaterial,
  Vector3,
  type Material,
  type NodeMaterial,
  type Object3D,
} from 'three/webgpu';
import {
  attribute,
  cameraProjectionMatrix,
  cameraViewMatrix,
  float,
  materialEmissive,
  modelWorldMatrix,
  normalLocal,
  positionGeometry,
  positionWorld,
  uniform,
  uv,
  vec3,
  vec4,
} from 'three/tsl';
import {
  EntityKind,
  heightAt,
  type Heightfield,
  type HymnSpawnDef,
  type MapDef,
  type Vec3,
} from '@stillsun/shared';
import { requestLatePrecompile, type QualityTier } from './renderer.js';
import { sound } from '../audio/sound.js';
import type { LoopHandle } from '../audio/sound.js';

/**
 * The **life layer**: the cheap instanced motion that makes a static world read as inhabited.
 *
 * Four systems, none of which the CPU touches per entity:
 *
 *  1. **Lantern flicker** — one band-limited envelope multiplied into the merged `lantern` mesh's
 *     emissive, varied per lantern by the fragment's own world position. One material.
 *  2. **Banner cloth** — a travelling wave down the merged `cloth` mesh, pinned at the attached
 *     edge by the vertex's own `uv.x`. One material, one mesh, one draw.
 *  3. **Carrion birds** — ONE instanced draw for the whole game. Two colonies circle two sites;
 *     every orbit, bob and wingbeat is a function of one clock and five per-instance floats, so
 *     the CPU writes nothing per frame and the flock costs exactly one render object.
 *  4. **The Choir, audible before visible** — one spatial loop per monster *camp* (not per
 *     monster), started and stopped by distance so only the nearest few ever play.
 *
 * ## The budget this is built to (docs/PERF-BUDGET.md)
 *
 * - **+1 render object, total.** The birds. The `cloth` and `lantern` meshes are already inside
 *   the region GLB's per-material merge and are counted there; this module re-materials them, it
 *   does not add them. Nothing here is a per-entity `Object3D`, nothing scales with content.
 * - **No lights, ever** (rule 3). A lantern is an emissive material. Three hashes the visible
 *   light set into every material's cache key, so one light appearing or disappearing rebuilds
 *   every material in the scene; a flickering *light* would do that sixty times a second. The
 *   flicker therefore moves an emissive term and nothing else.
 * - **Two uniform writes a frame** (rule: one clock). `clock` drives all three visual systems —
 *   lanterns, cloth and birds share the same node — and `night` is a slow scalar for the
 *   lanterns' night lift. That is the entire per-frame CPU cost of the visual half of this file.
 * - **Warmed before it is on screen** (rule 5). `compileAsync` culls exactly like a render, and
 *   the budget doc already records two late compiles from precisely this mistake on the NPCs'
 *   lantern meshes. Every mesh this module owns or re-materials gets `frustumCulled = false`
 *   (which costs nothing: a *merged* lantern/cloth mesh's bounding sphere already spans the map,
 *   so the object-level test never rejected it anyway), and `attachRegion` asks for a late
 *   precompile because the region GLB lands after the deploy's.
 *
 * ## Failure is soft, everywhere
 *
 * A missing `cloth` mesh, a GLB with no UVs, a TSL graph that will not build on some backend, a
 * map with no `hymnSpawns`, an `AudioContext` that has not been unlocked yet: each of those
 * silently costs its own feature and nothing else. `?life=0` switches the whole layer off, which
 * is what the perf probe needs to attribute a hitch to it (WEBGPU-NOTES §1).
 */

// --------------------------------------------------------------------------------- tuning

/** Lantern flicker: the mean level, the swing either side of it, and the night lift. */
const FLICKER_MEAN = 1;
const FLICKER_SWING = 0.15;
/** Three incommensurate rates, in radians per second of clock. Slow — a hearth, not a strobe. */
const FLICKER_RATE_A = 1.7;
const FLICKER_RATE_B = 2.63;
const FLICKER_RATE_C = 4.19;
/** How much brighter a lantern reads at the deep of night than at noon. */
const LANTERN_NIGHT_LIFT = 0.45;
/**
 * Per-lantern phase, in radians per metre of world position. Low enough that one pane of glass is
 * effectively at a single phase, high enough that two lanterns a few metres apart are unrelated —
 * see the note in `applyLanternFlicker` for why this is a plane and not a hash.
 */
const FLICKER_PHASE_X = 0.61;
const FLICKER_PHASE_Z = 0.43;
const FLICKER_PHASE_Y = 0.27;

/** Cloth: metres of travel at the free edge, wave rate, and crests per metre down the banner. */
const CLOTH_AMP = 0.16;
const CLOTH_RATE = 2.1;
const CLOTH_FREQ_V = 5.5;
/** Spatial phase, radians per metre, so two banners twenty metres apart never wave in unison. */
const CLOTH_FREQ_WORLD = 0.35;

/** Birds per colony, by tier. A colony is a silhouette, not a census. */
const BIRDS_PER_SITE: Record<QualityTier, number> = { low: 8, pc: 14, ultra: 14 };
/** Orbit radius range, metres. */
const ORBIT_R_MIN = 11;
const ORBIT_R_MAX = 27;
/** Angular speed range, rad/s: one lazy turn every 50-110 s. */
const ORBIT_W_MIN = 0.057;
const ORBIT_W_MAX = 0.125;
/** Altitude band above the ground under the bird's own orbit centre, metres. */
const ALT_MIN = 18;
const ALT_MAX = 30;
/** Vertical bob: metres of rise and fall, and its rate in rad/s. */
const BOB_AMP_MIN = 0.5;
const BOB_AMP_MAX = 1.7;
const BOB_RATE = 0.41;
/** Wingspan range, metres. A carrion bird is big; these are read at 60-250 m. */
const WING_SPAN_MIN = 1.3;
const WING_SPAN_MAX = 1.9;
/** Wingbeat: rad/s, and the tip's travel as a fraction of the quad's half-height. */
const FLAP_RATE = 3.4;
const FLAP_AMP = 0.34;
/** A bird is a black notch. Unlit, unfogged — see `birdMaterial`. */
const BIRD_COLOR = 0x0d0b11;

/**
 * The colonies, per map. Two over the Vigil Downs: the Tithe Barrows, where the digging is, and
 * the Watch Hill, where the Choir's Sexton stands. Both are named in `maps/vigil-downs.ts`; the
 * numbers here are the sites' XZ, and the altitude comes off the heightfield under each bird.
 */
const FLOCK_SITES: Readonly<Record<string, readonly (readonly [number, number])[]>> = {
  'vigil-downs': [
    [150, 60], // the Tithe Barrows
    [180, -180], // the Watch Hill
  ],
};

/** Two Hymn spawns within this of each other are the same camp. Camps are ~10 m across. */
const CAMP_LINK_M = 30;
/** How many camps may be sounding at once. The rest are silent however close the fifth is. */
const CAMP_VOICES = 4;
/**
 * Where a camp bed fades in, and where it is at full weight. The panner's own inverse curve is
 * clamped at `MAX_DISTANCE` (80 m, audio/sound.ts), so past that a camp neither gets quieter nor
 * disappears; this envelope is what actually takes it to silence, and it is deliberately far —
 * hearing the Choir over a ridge at 150 m before you can see it is the whole point of the system.
 */
const CAMP_FAR_M = 150;
const CAMP_FULL_M = 80;
/** Weight of each layer, as a multiplier on the clip's own manifest gain. */
const CAMP_VOICE_GAIN = 1;
const CAMP_DRONE_GAIN = 0.9;
/**
 * Close in, `entities.ts` drives the *real* Sexton's `sextonDrone()` off the snapshot, so the
 * camp's bed would be a second copy of the same organ. It ducks to this over the last 30 m
 * instead of stopping, so an empty camp (every Sexton dead) still hums.
 */
const CAMP_DRONE_DUCK = 0.3;
const CAMP_DUCK_NEAR_M = 25;
const CAMP_DUCK_FAR_M = 55;
/** Fades, ms. Long enough that a camp entering the nearest-four never clicks in. */
const CAMP_FADE_IN_MS = 900;
const CAMP_FADE_OUT_MS = 700;
/** How often the camp set is re-chosen. Ten distances at 4 Hz; the ear cannot hear finer. */
const CAMP_TICK_S = 0.25;

/** The clock wraps here so a long session never loses sine precision. 2pi * 4096. */
const CLOCK_WRAP = Math.PI * 8192;

/**
 * TSL's published types widen `attribute(name, type)`'s parameter to `string`, which loses every
 * swizzle on the result. Same escape hatch render/grass.ts, render/decals.ts and render/sky.ts use.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyNode = any;

/** `?life=0` — the kill switch every pass owes the probe (WEBGPU-NOTES §1). */
function lifeEnabled(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('life') !== '0';
  } catch {
    return true;
  }
}

/** Deterministic pseudo-random, so a flock is identical between reloads and between clients. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0x100000000;
  };
}

/** A stable small number per map id, so two maps' lanterns never flicker in lockstep. */
function mapPhase(mapId: string): number {
  let h = 2166136261;
  for (let i = 0; i < mapId.length; i++) {
    h ^= mapId.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) / 0x100000000) * Math.PI * 2;
}

// --------------------------------------------------------------------------------- public shape

export interface LifeOptions {
  /**
   * The map being brought up. `hymnSpawns` seeds the Choir beds, `id` picks the flock sites and
   * seeds the per-map flicker phase.
   */
  map: MapDef;
  /**
   * The sim's heightfield, when the map has one. Only the flock reads it, and only to put each
   * bird its own altitude above the ground it is circling — the Watch Hill's colony would
   * otherwise orbit inside the hill.
   */
  heightfield?: Heightfield | null;
  tier: QualityTier;
}

export interface LifeHandle {
  /**
   * The one `Object3D` this module adds to the scene graph: a `Group` holding the bird flock and
   * nothing else. Parent it to `mapRoot`, at identity — the birds' billboard is built through
   * `modelWorldMatrix`, so a transform is honoured, but nothing else here expects one.
   *
   * Its `userData.dispose` is wired the way `render/grass.ts` wires the grass sheet's, so
   * `setMap`'s existing teardown loop disposes the whole layer (loops included) on a map swap
   * without scene.ts knowing this module exists.
   */
  readonly group: Group;
  /**
   * Hands the layer the region GLB once it has attached, so the merged `cloth` and `lantern`
   * meshes inside it get their animation. Safe to call with either half of the attachment, more
   * than once, and with a subtree that contains neither name. Asks for a late precompile itself.
   */
  attachRegion(root: Object3D | null): void;
  /**
   * One frame. `viewer` is the *character's* world position (the same one `grass.update` takes —
   * a third-person camera sits several metres behind the ears the Choir is being mixed for), and
   * `daylight` is `sky.keyIntensity`: 1 at the top of the day, 0 at the deep of night.
   *
   * Cost: two uniform writes, plus — four times a second, not per frame — ten distances and at
   * most a handful of `GainNode` ramps.
   */
  update(dt: number, viewer: Vector3, daylight: number): void;
  dispose(): void;
}

/**
 * Builds the life layer for one map.
 *
 * ### Where this goes in `render/scene.ts`
 *
 * Inside `setMap`, next to the grass block (it wants the same `hf`), after the terrain is added:
 *
 * ```ts
 * life = createLife({ map: m, heightfield: hf ?? null, tier });
 * mapRoot.add(life.group);
 * ```
 *
 * and in the region GLB's `.then(...)`, immediately before the existing
 * `requestLatePrecompile('spine-region')`:
 *
 * ```ts
 * life.attachRegion(region);
 * life.attachRegion(wreck);   // whichever GLB carries the `cloth` / `lantern` merges
 * ```
 *
 * `life` is cleared by the same `mapRoot` teardown that clears the grass (via
 * `group.userData.dispose`), so `setMap` needs no explicit disposal; expose it on `SceneHandle`
 * the way `grass` is exposed, and the frame loop in `main.ts` owes it one call:
 *
 * ```ts
 * scene.life?.update(dt, characterWorldPosition, scene.sky.keyIntensity);
 * ```
 */
export function createLife(opts: LifeOptions): LifeHandle {
  const group = new Group();
  group.name = 'life';
  // The layer holds one instanced mesh and re-materials meshes it does not own; it never needs a
  // transform of its own, and `scene.matrixWorldAutoUpdate` is off, so keep it at identity.
  group.matrixAutoUpdate = false;

  const enabled = lifeEnabled();
  const phase = mapPhase(opts.map.id);

  // The one clock every visual system in this file reads, and the slow night scalar the lanterns
  // read on top of it. Uniforms, never node-graph edits: a rebuilt graph changes the material's
  // cache key and recompiles the shader mid-frame (WEBGPU-NOTES §1).
  const clock = uniform(phase);
  const night = uniform(0);

  const birds = enabled ? createFlock(opts, phase, clock) : null;
  if (birds) group.add(birds.mesh);

  const choir = enabled ? new ChoirBeds(campsOf(opts.map.hymnSpawns ?? [])) : null;

  /** Every material this module has already touched, so a re-attach is a no-op rather than a
   * second `emissiveNode` wrapped around the first. */
  const touched = new WeakSet<object>();
  let dead = false;

  const handle: LifeHandle = {
    group,

    attachRegion(root: Object3D | null): void {
      if (!enabled || dead || root === null) return;
      let found = false;
      root.traverse((o) => {
        // Duck-typed, not `instanceof`: this subtree came in through GLTFLoader (see region.ts).
        const mesh = o as Mesh;
        if (!mesh.isMesh) return;
        const material: Material | Material[] = mesh.material;
        // A merged mesh has exactly one material by construction (one merge per material class),
        // and a material this module has already wrapped is never wrapped twice.
        if (Array.isArray(material) || touched.has(material)) return;
        const node = material as NodeMaterial;
        const lantern = mesh.name === 'lantern' || mesh.name.startsWith('lantern_');
        const cloth = mesh.name === 'cloth' || mesh.name.startsWith('cloth_');
        if (!lantern && !cloth) return;
        const ok = lantern
          ? applyLanternFlicker(node, clock, night, phase)
          : applyClothWave(mesh, node, clock, phase);
        if (!ok) return;
        touched.add(material);
        warmForPrecompile(mesh);
        found = true;
      });
      // The region GLB lands after the deploy's precompile, so the two graphs above are new
      // pipelines that would otherwise compile on the first frame that frames a banner — the
      // 2-4 s freeze WEBGPU-NOTES §1 is about. Coalesced 300 ms in renderer.ts.
      if (found) requestLatePrecompile('life');
    },

    update(dt: number, viewer: Vector3, daylight: number): void {
      if (!enabled || dead) return;
      // Write 1 of 2. Everything that moves in this file is a function of this number.
      clock.value += dt;
      if (clock.value > CLOCK_WRAP) clock.value -= CLOCK_WRAP;
      // Write 2 of 2. A scalar, not a light: rule 3.
      night.value = 1 - (daylight < 0 ? 0 : daylight > 1 ? 1 : daylight);
      choir?.update(dt, viewer);
    },

    dispose(): void {
      if (dead) return;
      dead = true;
      choir?.dispose();
      if (birds) {
        group.remove(birds.mesh);
        birds.dispose();
      }
      // The `cloth` / `lantern` materials belong to the region GLB's own attachment, which
      // disposes them through its `userData.dispose` (render/region.ts). Touching them here
      // would double-dispose them.
    },
  };

  // The scene teardown in render/scene.ts disposes whatever a `mapRoot` child hangs here — the
  // same contract render/grass.ts uses, and the reason `setMap` needs no code for this module.
  group.userData['dispose'] = () => {
    handle.dispose();
  };
  return handle;
}

/**
 * Rule 5, applied to a mesh this module did not create. `compileAsync` walks the scene exactly
 * the way a render does, so a banner behind the camera at precompile time compiles later, on
 * screen, as a freeze — which is what the two late `MeshStandardNodeMaterial` compiles recorded
 * in docs/PERF-BUDGET.md were.
 *
 * Turning the object-level frustum test off costs nothing here and is not a licence taken
 * lightly: these are *merged* meshes, one per material for the whole map, so their bounding
 * sphere already spans the region and the test never rejected them in the first place.
 */
function warmForPrecompile(mesh: Mesh): void {
  // Per object, not on an ancestor: `compileAsync`'s `_projectObject` tests `frustumCulled` on
  // the mesh it is about to push and never on a parent (game/npcs.ts learned this the hard way).
  mesh.frustumCulled = false;
}

// ------------------------------------------------------------------------------ 1. lanterns

/**
 * Multiplies a band-limited flicker into one lantern material's emissive.
 *
 * Three things this is careful about:
 *
 *  - **It never creates or toggles a light** (rule 3). The whole effect is one scalar inside an
 *    emissive term.
 *  - **It composes rather than replaces.** `render/materials.ts` may already have given the glass
 *    an `emissiveNode` (a fresnel rim, its own envelope); this wraps whatever is there instead of
 *    overwriting it, and only builds an emissive from `material.emissive` when there is none.
 *  - **Per-lantern variation costs no attribute.** The phase comes out of a hash of the
 *    fragment's own world position, so one material serves every lantern on the map and no two
 *    of them breathe together. A material *per lantern* would be dozens of pipelines.
 *
 * Returns false if the TSL graph would not build, in which case the lantern keeps whatever
 * steady emissive it already had — the house rule everywhere in `render/`: a lit lantern that
 * does not flicker beats a black box.
 */
function applyLanternFlicker(
  material: NodeMaterial,
  clock: AnyNode,
  night: AnyNode,
  phase: number,
): boolean {
  try {
    const t = clock as AnyNode;
    const p = positionWorld as unknown as AnyNode;

    // Per-lantern phase, out of the fragment's own world position and costing no attribute.
    //
    // Deliberately a **low-frequency plane**, not a `sin`/`fract` hash. This runs per *fragment*,
    // and a hash would decorrelate every pixel of a lantern's glass from its neighbour: the pane
    // would sparkle as noise instead of guttering as one flame. At these frequencies the phase
    // moves ~0.2 rad across a 30 cm pane (invisible) and several radians between lanterns metres
    // apart (which is the whole variation this is for). It is also seam-free, which a quantised
    // hash would not be for a lantern straddling a cell boundary.
    const seed: AnyNode = p.x
      .mul(FLICKER_PHASE_X)
      .add(p.z.mul(FLICKER_PHASE_Z))
      .add(p.y.mul(FLICKER_PHASE_Y))
      .add(phase);

    // Band-limited: three rates with no small common factor, so the sum's period is long enough
    // that the eye never locks onto it, and there is nothing above ~0.7 Hz in it — a hearth
    // guttering, not a fluorescent tube.
    const a: AnyNode = t.mul(FLICKER_RATE_A).add(seed).sin().mul(0.5);
    const b: AnyNode = t.mul(FLICKER_RATE_B).add(seed.mul(1.7)).sin().mul(0.32);
    const c: AnyNode = t.mul(FLICKER_RATE_C).add(seed.mul(2.9)).sin().mul(0.18);
    // +-FLICKER_SWING about the mean, lifted at night so a lantern reads as the light source it
    // is once the sun is under the map. `night` is a uniform, so this is still one graph.
    const lift: AnyNode = float(1).add((night as AnyNode).mul(LANTERN_NIGHT_LIFT));
    const envelope: AnyNode = float(FLICKER_MEAN).add(a.add(b).add(c).mul(FLICKER_SWING)).mul(lift);

    // `materialEmissive` is three's own live reference to `emissive * emissiveIntensity` — both
    // uniforms, re-read from the material every frame — so wrapping it is literally animating
    // `emissiveIntensity`, and whatever `render/materials.ts` sets those to still lands.
    // `emissiveNode` lives on the standard/physical node materials rather than on the
    // `NodeMaterial` base, and this function is deliberately typed against the base so a lantern
    // the region GLB happens to give some other node material still gets its flicker.
    const emissive = material as unknown as { emissiveNode?: AnyNode };
    const existing = emissive.emissiveNode;
    emissive.emissiveNode =
      existing !== null && existing !== undefined
        ? existing.mul(envelope)
        : (materialEmissive as unknown as AnyNode).mul(envelope);
    return true;
  } catch (err) {
    console.warn('[stillsun] TSL lantern flicker unavailable; lanterns burn steady', err);
    return false;
  }
}

// --------------------------------------------------------------------------------- 2. cloth

/**
 * A travelling wave down the merged banner mesh.
 *
 * The pin weight is the vertex's own `uv.x`: the Blender pass lays every banner out with u = 0
 * on the edge that is nailed to the beam and u = 1 on the free edge, so the amplitude falls to
 * exactly zero where the cloth is attached whatever direction that banner happens to face.
 * `local X` cannot do this job on a *merged* mesh — after the merge, local X is world X, which
 * says where the banner is, not which end of it is free.
 *
 * The displacement runs along `normalLocal`, i.e. out of the banner's face, for the same reason:
 * one mesh holds banners at the gate and in the Lantern Hall facing different ways, and a fixed
 * world axis would shear half of them.
 *
 * Returns false (and leaves the banner stiff) if the geometry carries no UVs.
 */
function applyClothWave(
  mesh: Mesh,
  material: NodeMaterial,
  clock: AnyNode,
  phase: number,
): boolean {
  if (!mesh.geometry.hasAttribute('uv')) {
    console.warn('[stillsun] cloth mesh has no UVs; banners stay stiff');
    return false;
  }
  try {
    const t = clock as AnyNode;
    const local = positionGeometry as unknown as AnyNode;
    const texel = uv() as unknown as AnyNode;
    const free: AnyNode = texel.x;
    const along: AnyNode = texel.y;

    // Spatial phase off the vertex's own position, so the gate's banners and the Hall's are not
    // one animation seen twice. Free, and it needs no attribute.
    const worldPhase: AnyNode = local.x.add(local.z).mul(CLOTH_FREQ_WORLD).add(phase);
    const wave: AnyNode = t.mul(CLOTH_RATE).add(along.mul(CLOTH_FREQ_V)).add(worldPhase).sin();
    // Quadratic in the free-edge weight: the nailed edge is pinned, the free edge carries the
    // whole travel, and the curve between them is what makes cloth billow rather than shear.
    const amp: AnyNode = wave.mul(free).mul(free).mul(CLOTH_AMP);
    material.positionNode = local.add((normalLocal as unknown as AnyNode).mul(amp));
    return true;
  } catch (err) {
    console.warn('[stillsun] TSL cloth wave unavailable; banners stay stiff', err);
    return false;
  }
}

// --------------------------------------------------------------------------------- 3. birds

interface Flock {
  readonly mesh: Mesh;
  dispose(): void;
}

/**
 * One bird: a gull notch in the XY plane, x in [-0.5, 0.5] (one wingspan) and y small. Four
 * triangles, twelve vertices, no texture and no alpha — the *shape* is the silhouette, so there
 * is nothing to blend, nothing to sort and no alpha test in the fragment stage.
 *
 * The wing weight the flap reads is `abs(x) * 2` computed in the shader rather than an
 * attribute: it is exactly what the layout already encodes.
 */
function birdGeometry(): InstancedBufferGeometry {
  // 0 at the body, 1 at a wing tip; z is 0 everywhere (the quad is oriented in view space).
  const tris: [number, number][][] = [
    // body: a small diamond, two triangles
    [
      [0, -0.06],
      [0.09, 0.02],
      [0, 0.1],
    ],
    [
      [0, -0.06],
      [0, 0.1],
      [-0.09, 0.02],
    ],
    // wings: a shallow gull sweep either side
    [
      [-0.09, 0.03],
      [-0.5, 0.17],
      [-0.15, -0.05],
    ],
    [
      [0.09, 0.03],
      [0.15, -0.05],
      [0.5, 0.17],
    ],
  ];
  const positions = new Float32Array(tris.length * 3 * 3);
  let o = 0;
  for (const tri of tris) {
    for (const v of tri) {
      positions[o++] = v[0];
      positions[o++] = v[1];
      positions[o++] = 0;
    }
  }
  const geo = new InstancedBufferGeometry();
  geo.setAttribute('position', new BufferAttribute(positions, 3));
  // No normals and no UVs: the material is unlit and untextured, so both would be attribute
  // streams fetched per vertex for nothing.
  return geo;
}

/**
 * The flock: **one** instanced draw for every colony on the map, and therefore one render object
 * however many sites the map grows.
 *
 * All of the motion is in the vertex stage, read from one clock uniform and two per-instance
 * vec4s. Nothing here is written after construction — no matrix upload, no per-bird loop, no
 * sort. The CPU cost of a flock in flight is zero.
 *
 * ## Why an `InstancedBufferGeometry` on a plain `Mesh`, and not an `InstancedMesh`
 *
 * `NodeMaterial.setup` calls `setupVertex` — and therefore `setupPosition`, and therefore
 * `instancedMesh(object)` — *unconditionally*, and only then picks `this.vertexNode || mvp` for
 * the clip position (three r185, NodeMaterial.js ~line 481). So an `InstancedMesh` with a
 * `vertexNode` still pays for three's built-in instance-matrix path: a 64-byte-per-instance
 * buffer, a matrix fetch and a multiply per vertex, feeding a `positionLocal` this file's
 * billboard never reads. `geometry.isInstancedBufferGeometry` takes the same instanced draw
 * (`RenderObject.getDrawParameters`) without any of that.
 *
 * ## Why the billboard is written out by hand
 *
 * TSL's `billboarding()` helper builds its quad from `positionLocal`, and this file needs to bend
 * the wings *before* the quad is oriented. Reading a mutated `positionLocal` back inside a helper
 * works only by assignment ordering, and a wrong guess there is a silently mis-transformed vertex
 * or a shader that fails to build with no JavaScript signal (WEBGPU-NOTES §1) — the same trap
 * render/grass.ts documents. So the centre goes to view space, the wing offsets are added there
 * (view space is metric, so a wingspan in metres stays a wingspan in metres), and the projection
 * is applied last.
 */
function createFlock(opts: LifeOptions, phase: number, clock: AnyNode): Flock | null {
  const sites = FLOCK_SITES[opts.map.id];
  if (!sites || sites.length === 0) return null;
  const per = BIRDS_PER_SITE[opts.tier];
  const count = sites.length * per;
  if (count === 0) return null;

  const hf = opts.heightfield ?? null;
  const geometry = birdGeometry();
  const orbit = new Float32Array(count * 4);
  const bird = new Float32Array(count * 4);
  const rand = rng(0x5715_0a11);
  let i = 0;
  for (const site of sites) {
    const [sx, sz] = site;
    for (let n = 0; n < per; n++) {
      const radius = ORBIT_R_MIN + rand() * (ORBIT_R_MAX - ORBIT_R_MIN);
      const a = rand() * Math.PI * 2;
      // Each bird orbits its own centre, jittered off the site, so the colony reads as a loose
      // kettle rather than as one ring of beads.
      const cx = sx + (rand() * 2 - 1) * 6;
      const cz = sz + (rand() * 2 - 1) * 6;
      const ground = hf ? heightAt(hf, cx, cz) : 0;
      orbit[i * 4] = cx;
      orbit[i * 4 + 1] = cz;
      orbit[i * 4 + 2] = radius;
      // Half the colony turns the other way; a kettle is not a carousel.
      orbit[i * 4 + 3] =
        (ORBIT_W_MIN + rand() * (ORBIT_W_MAX - ORBIT_W_MIN)) * (rand() < 0.5 ? -1 : 1);
      bird[i * 4] = ground + ALT_MIN + rand() * (ALT_MAX - ALT_MIN);
      bird[i * 4 + 1] = a + phase;
      bird[i * 4 + 2] = BOB_AMP_MIN + rand() * (BOB_AMP_MAX - BOB_AMP_MIN);
      bird[i * 4 + 3] = WING_SPAN_MIN + rand() * (WING_SPAN_MAX - WING_SPAN_MIN);
      i++;
    }
  }
  geometry.setAttribute('iOrbit', new InstancedBufferAttribute(orbit, 4));
  geometry.setAttribute('iBird', new InstancedBufferAttribute(bird, 4));

  // Unlit on purpose, and for the same reason the grass is: a `MeshBasicNodeMaterial` builds no
  // lights node, so this shader does not depend on how many lights are in the scene and cannot be
  // re-keyed by one changing (rule 3). `fog: false` because the vertex position is written by
  // `vertexNode` and three's fog term is derived from the *unmodified* model-view position, which
  // for this mesh is the world origin — a bird would fog by the player's distance from (0,0,0).
  // A carrion bird reading as a hard black notch against the sky is also just correct.
  const material = new MeshBasicNodeMaterial({ side: DoubleSide, fog: false });
  material.color.setHex(BIRD_COLOR);

  let shaded = true;
  try {
    const t = clock as AnyNode;
    const local = positionGeometry as unknown as AnyNode;
    const o = attribute('iOrbit', 'vec4') as unknown as AnyNode;
    const b = attribute('iBird', 'vec4') as unknown as AnyNode;

    const ang: AnyNode = t.mul(o.w).add(b.y);
    const centre: AnyNode = vec3(
      o.x.add(ang.cos().mul(o.z)),
      b.x.add(t.mul(BOB_RATE).add(b.y.mul(1.7)).sin().mul(b.z)),
      o.y.add(ang.sin().mul(o.z)),
    );

    // The wingbeat, quadratic in the distance out along the wing so the tips carry it and the
    // body does not shear. One more sine on the same clock.
    const wing: AnyNode = local.x.abs().mul(2);
    const flap: AnyNode = t.mul(FLAP_RATE).add(b.y.mul(3.1)).sin();
    const lift: AnyNode = flap.mul(wing).mul(wing).mul(FLAP_AMP);
    const span = b.w;

    const centreView: AnyNode = (cameraViewMatrix as unknown as AnyNode)
      .mul(modelWorldMatrix as unknown as AnyNode)
      .mul(vec4(centre, 1));
    const viewPos: AnyNode = centreView.xyz.add(
      vec3(local.x.mul(span), local.y.add(lift).mul(span), float(0)),
    );
    material.vertexNode = (cameraProjectionMatrix as unknown as AnyNode).mul(vec4(viewPos, 1));
  } catch (err) {
    // Without the vertex graph every instance would draw at the world origin, stacked. A flock
    // that is not there beats a black smear over the plaza.
    console.warn('[stillsun] TSL bird flock unavailable; the sky stays empty', err);
    shaded = false;
  }

  // `instanceCount` is the whole instancing declaration: one `drawIndexed(_, count)`, one render
  // object, and none of `InstancedMesh`'s per-instance matrix machinery (see the header).
  geometry.instanceCount = shaded ? count : 0;

  const mesh = new Mesh(geometry, material);
  mesh.name = 'life:birds';
  mesh.visible = shaded;
  // Two tiny clusters whose positions live in the shader, so there is no CPU-side bounding volume
  // that could ever be right — and turning the test off is also what warms the pipeline before
  // the flock is first framed (rule 5).
  mesh.frustumCulled = false;
  // A bird 25 m up at a 4.5 degree sun throws its shadow half a kilometre sideways onto nothing.
  mesh.castShadow = false;
  mesh.receiveShadow = false;

  return {
    mesh,
    dispose(): void {
      geometry.dispose();
      material.dispose();
    },
  };
}

// ---------------------------------------------------------------------------------- 4. Choir

/** One monster camp: where it is, and whether anything in it is a Sexton. */
interface Camp {
  readonly pos: Vec3;
  readonly sexton: boolean;
}

/**
 * Groups a map's Hymn spawns into camps by single-linkage at `CAMP_LINK_M`.
 *
 * Derived rather than hard-coded because it has to stay true: `maps/vigil-downs.ts` lays out ten
 * camps as ten blocks of `addHymn` calls, and a designer moving one — or adding an eleventh — must
 * not have to remember there is a table of coordinates in the render layer. Runs once per map
 * swap over ~35 spawns.
 */
function campsOf(spawns: readonly HymnSpawnDef[]): Camp[] {
  const owner = new Int32Array(spawns.length).fill(-1);
  const camps: { x: number; y: number; z: number; n: number; sexton: boolean }[] = [];
  for (let i = 0; i < spawns.length; i++) {
    const s = spawns[i];
    if (!s) continue;
    // Single linkage: join the first camp any *member* is close to. Spawns are authored in
    // per-camp blocks, so this walks each block into one cluster in order.
    let target = -1;
    for (let j = 0; j < i; j++) {
      const other = spawns[j];
      const owned = owner[j] ?? -1;
      if (!other || owned < 0) continue;
      const dx = other.pos.x - s.pos.x;
      const dz = other.pos.z - s.pos.z;
      if (dx * dx + dz * dz <= CAMP_LINK_M * CAMP_LINK_M) {
        target = owned;
        break;
      }
    }
    if (target < 0) {
      target = camps.length;
      camps.push({ x: 0, y: 0, z: 0, n: 0, sexton: false });
    }
    const camp = camps[target];
    if (!camp) continue;
    camp.x += s.pos.x;
    camp.y += s.pos.y;
    camp.z += s.pos.z;
    camp.n += 1;
    if (s.kind === EntityKind.Sexton) camp.sexton = true;
    owner[i] = target;
  }
  return camps.map((c) => ({
    // Ear height above the camp's mean footing, so the bed is not mixed from the dirt.
    pos: { x: c.x / c.n, y: c.y / c.n + 1.4, z: c.z / c.n },
    sexton: c.sexton,
  }));
}

interface Bed {
  readonly camp: Camp;
  voice: LoopHandle | null;
  drone: LoopHandle | null;
  /** Last gain multiplier written, so a steady-state tick writes no ramps at all. */
  level: number;
}

/**
 * The Choir, audible before it is visible.
 *
 * **One loop per camp, not one per monster.** A camp is three or four Hymns; ten camps is
 * thirty-odd entities, and thirty voices of the same chant layered on each other is mud as well
 * as a voice-cap problem (`MAX_VOICES` is 24 for the whole game). A camp gets one chorister bed,
 * plus the Sexton's organ drone under it when the camp holds one.
 *
 * **Only the nearest four ever exist.** Loops are started when a camp enters the set and stopped
 * with a fade when it leaves, so the steady-state cost is at most eight `AudioBufferSourceNode`s
 * however big the map's roster grows — the audio equivalent of the render budget's "never let a
 * pass scale with content".
 *
 * The lifecycle is `audio/sound.ts`'s own, followed exactly: `startLoop(id, pos)` for a
 * spatialised bed, `setGain(mult, fadeMs)` for every level change (never a raw `GainNode`), and
 * `stop(fadeMs)` to release it. Loops are started at gain 0 and faded up for the reason
 * `setDimming`/`beacon` document — a bed that starts at level from a cold buffer clicks.
 *
 * `sound.reset()` does not know about these (it only reaps the loops it manages itself), so
 * `dispose()` is what stops them, and it is wired to the `mapRoot` teardown through
 * `group.userData.dispose`.
 */
class ChoirBeds {
  private readonly beds: Bed[];
  private since = CAMP_TICK_S;
  /** Scratch, reused: `update` allocates nothing. */
  private readonly order: number[];

  constructor(camps: readonly Camp[]) {
    this.beds = camps.map((camp) => ({ camp, voice: null, drone: null, level: 0 }));
    this.order = this.beds.map((_, i) => i);
  }

  update(dt: number, viewer: Vector3): void {
    if (this.beds.length === 0) return;
    this.since += dt;
    if (this.since < CAMP_TICK_S) return;
    this.since = 0;

    // Nearest first. Ten entries; an insertion sort over a reused index array allocates nothing.
    for (let a = 1; a < this.order.length; a++) {
      const key = this.order[a] ?? 0;
      const kd = this.distanceTo(key, viewer);
      let b = a - 1;
      while (b >= 0 && this.distanceTo(this.order[b] ?? 0, viewer) > kd) {
        this.order[b + 1] = this.order[b] ?? 0;
        b--;
      }
      this.order[b + 1] = key;
    }

    for (let rank = 0; rank < this.order.length; rank++) {
      const idx = this.order[rank] ?? 0;
      const bed = this.beds[idx];
      if (!bed) continue;
      const d = this.distanceTo(idx, viewer);
      // Past the fourth camp, or past the horizon, this camp is silent whatever its distance
      // envelope says. That cap is the whole reason this scales.
      const level = rank < CAMP_VOICES ? envelopeAt(d) : 0;
      if (level <= 0) {
        this.silence(bed);
        continue;
      }
      this.raise(bed, d, level);
    }
  }

  /** Horizontal distance from the listener to camp `i`. Height is deliberately ignored: a camp
   * on the Watch Hill is not further away for being higher up. */
  private distanceTo(i: number, viewer: Vector3): number {
    const bed = this.beds[i];
    if (!bed) return Infinity;
    return Math.hypot(bed.camp.pos.x - viewer.x, bed.camp.pos.z - viewer.z);
  }

  /** Starts a camp's loops if they are not running, then writes their levels. */
  private raise(bed: Bed, d: number, level: number): void {
    const fresh = bed.voice === null;
    if (fresh) {
      // Before the first user gesture `startLoop` hands back a permanently dead handle, so do
      // not take one: the next tick (250 ms) tries again, and by then Enter has been pressed.
      if (!sound.unlocked) return;
      bed.voice = sound.startLoop('chorister_voice', bed.camp.pos);
      bed.voice.setGain(0);
      if (bed.camp.sexton) {
        bed.drone = sound.startLoop('sexton_drone', bed.camp.pos);
        bed.drone.setGain(0);
      }
      bed.level = 0;
    }
    // A ramp is a scheduled `AudioParam` event; writing one every tick for a level that has not
    // moved is the audio version of re-uploading an unchanged buffer.
    if (!fresh && Math.abs(level - bed.level) < 0.02) return;
    const fade = fresh ? CAMP_FADE_IN_MS : CAMP_TICK_S * 1000 * 1.6;
    bed.voice?.setGain(level * CAMP_VOICE_GAIN, fade);
    // Close in, the live Sexton's own drone (entities.ts) is the sound; the camp bed ducks under
    // it rather than stopping, so a camp whose Sexton is dead still hums.
    const duck =
      CAMP_DRONE_DUCK +
      (1 - CAMP_DRONE_DUCK) *
        clamp01((d - CAMP_DUCK_NEAR_M) / (CAMP_DUCK_FAR_M - CAMP_DUCK_NEAR_M));
    bed.drone?.setGain(level * CAMP_DRONE_GAIN * duck, fade);
    bed.level = level;
  }

  private silence(bed: Bed): void {
    if (bed.voice === null && bed.drone === null) return;
    bed.voice?.stop(CAMP_FADE_OUT_MS);
    bed.drone?.stop(CAMP_FADE_OUT_MS);
    bed.voice = null;
    bed.drone = null;
    bed.level = 0;
  }

  dispose(): void {
    for (const bed of this.beds) {
      bed.voice?.stop(0);
      bed.drone?.stop(0);
      bed.voice = null;
      bed.drone = null;
    }
    this.beds.length = 0;
  }
}

/**
 * A camp's far-field envelope, 0 at `CAMP_FAR_M` and 1 by `CAMP_FULL_M`.
 *
 * This exists because the panner's inverse curve is *clamped* at `MAX_DISTANCE` (80 m) — past
 * that a camp holds a constant, faint level forever instead of fading out, so without an
 * envelope every camp on the map would be quietly audible from anywhere on it. Smoothstepped so
 * a camp does not switch on at a hard edge as the player walks a ridge line.
 */
function envelopeAt(d: number): number {
  const t = clamp01((CAMP_FAR_M - d) / (CAMP_FAR_M - CAMP_FULL_M));
  return t * t * (3 - 2 * t);
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
